import React, { useRef, useMemo, useState } from 'react';
import { Row, Col, Button, Spinner, Form, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSync, faSearch } from '@fortawesome/free-solid-svg-icons';
import AlertRuleCard from './AlertRuleCard';
import AlertRuleForm from './AlertRuleForm';
import DeleteAlertRuleModal from './DeleteAlertRuleModal';
import AlertRuleFilterBar from './AlertRuleFilterBar';
import { useAlertRules } from '../hooks/useAlertRules';
import { usePermission } from '../../../../hooks/usePermission';
import '../styles/AlertRules.css';


const AlertRulesList = ({ meters, sources, currentFinancialYear }) => {
  const [canCreateRule] = usePermission('iot-meters.create-alert-rule', 'IOT_METERS');
  const [canUpdateRule] = usePermission('iot-meters.update-alert-rule', 'IOT_METERS');
  const [canDeleteRule] = usePermission('iot-meters.delete-alert-rule', 'IOT_METERS');

  const { data: alertRules = [], isLoading, error, refetch } = useAlertRules();
  const alertRuleFormRef = useRef(null);
  const deleteModalRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    sourceIds: [],
    subLocationIds: [],
    modules: [],
    categories: [],
    subCategories: [],
    kpis: [],
    meterIds: [],
    enabledStatus: null // null = all, true = enabled, false = disabled
  });

  // Filter alert rules based on search and filters
  const filteredAlertRules = useMemo(() => {
    let result = alertRules;

    console.log('alertRules', alertRules);

    // Search by name
    if (searchTerm) {
      result = result.filter(rule =>
        rule.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by criteria
    if (filters.sourceIds.length > 0) {
      result = result.filter(rule =>
        rule.criteria?.sourceId?.some(id => filters.sourceIds.includes(id))
      );
    }

    if (filters.modules.length > 0) {
      result = result.filter(rule =>
        filters.modules.includes(rule.criteria?.module)
      );
    }

    if (filters.categories.length > 0) {
      result = result.filter(rule =>
        rule.criteria?.category?.some(cat => filters.categories.includes(cat))
      );
    }

    if (filters.subCategories.length > 0) {
      result = result.filter(rule =>
        rule.criteria?.subCategory?.some(subCat => filters.subCategories.includes(subCat))
      );
    }

    if (filters.kpis.length > 0) {
      result = result.filter(rule =>
        rule.criteria?.kpi?.some(kpi => filters.kpis.includes(kpi))
      );
    }

    if (filters.meterIds.length > 0) {
      result = result.filter(rule =>
        rule.criteria?.meterId?.some(id => filters.meterIds.includes(id))
      );
    }

    // Filter by enabled status
    if (filters.enabledStatus !== null) {
      result = result.filter(rule => rule.isEnabled === filters.enabledStatus);
    }

    return result;
  }, [alertRules, searchTerm, filters]);

  const handleAddAlertRule = () => {
    alertRuleFormRef.current?.show();
  };

  const handleEditAlertRule = (rule) => {
    alertRuleFormRef.current?.show(rule);
  };

  const handleDeleteAlertRule = (rule) => {
    deleteModalRef.current?.show(rule);
  };

  const handleSave = () => {
    refetch();
  };

  const handleDelete = () => {
    refetch();
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      sourceIds: [],
      subLocationIds: [],
      modules: [],
      categories: [],
      subCategories: [],
      kpis: [],
      meterIds: [],
      enabledStatus: null
    });
    setSearchTerm('');
  };

  return (
    <div className="alert-rules-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <p className="text-muted mb-0">Configure alerts for your IoT meters</p>
        </div>
        {canCreateRule && (
          <Button variant="primary" onClick={handleAddAlertRule}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add Alert Rule
          </Button>
        )}
      </div>

      {/* Search Bar */}
      <div className="mb-3">
        <InputGroup>
          <InputGroup.Text>
            <FontAwesomeIcon icon={faSearch} />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search alert rules by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>
      </div>

      {/* Filters */}
      <AlertRuleFilterBar
        meters={meters}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onClearFilters={handleClearFilters}
        sources={sources}
        currentFinancialYear={currentFinancialYear}
      />

      {/* Error Alert */}
      {error && (
        <div className="alert alert-danger" role="alert">
          Failed to load alert rules. Please try again.
          <Button
            variant="outline-danger"
            size="sm"
            className="ms-3"
            onClick={() => refetch()}
          >
            <FontAwesomeIcon icon={faSync} className="me-1" /> Retry
          </Button>
        </div>
      )}

      {/* Alert Rules List */}
      <div className="alert-rules-list">
        {isLoading ? (
          <div className="text-center p-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading alert rules...</p>
          </div>
        ) : filteredAlertRules.length > 0 ? (
          <>
            <div className="mb-3 text-muted">
              Showing {filteredAlertRules.length} of {alertRules.length} alert rules
            </div>
            <Row>
              {filteredAlertRules.map(rule => (
                <Col md={6} lg={4} className="mb-4" key={rule.id}>
                  <AlertRuleCard
                    rule={rule}
                    onEdit={handleEditAlertRule}
                    onDelete={handleDeleteAlertRule}
                    canEdit={canUpdateRule}
                    canDelete={canDeleteRule}
                  />
                </Col>
              ))}
            </Row>
          </>
        ) : (
          <div className="text-center p-5 empty-state">
            <h4>No alert rules found</h4>
            <p>
              {alertRules.length === 0
                ? 'Get started by creating your first alert rule.'
                : 'Try adjusting your search or filters.'}
            </p>
            {alertRules.length === 0 ? (
              canCreateRule && (
                <Button
                  variant="primary"
                  onClick={handleAddAlertRule}
                  className="mt-2"
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Create Your First Alert Rule
                </Button>
              )
            ) : (
              <Button
                variant="outline-primary"
                onClick={handleClearFilters}
                className="mt-2"
              >
                <FontAwesomeIcon icon={faSync} className="me-2" />
                Clear Filters
              </Button>
            )}
          </div>
        )}
      </div>

      <AlertRuleForm
        ref={alertRuleFormRef}
        onSave={handleSave}
        meters={meters}
        sources={sources}
        currentFinancialYear={currentFinancialYear}
      />

      <DeleteAlertRuleModal
        ref={deleteModalRef}
        onDelete={handleDelete}
      />
    </div>
  );
};

export default AlertRulesList;